//#region Util Imports

import * as _ from 'underscore'

//#endregion

import { Activity, Enums } from '../../../../models/common'
import { OpAlarmDefGroup, OpAlarmGroup } from '../../../../models/core'
import { UserSessionService } from '../../../../services/user-session'
import { IAlarmDefinitionGroupRowEdit, IAlarmDefinitionGroupRowEditOptions, ITransmissionTypeCheckBox, TransmissionTypeCheckBoxValueChangedArgs } from './alarm-definition-group-row-edit.interfaces'
import { TypeHelper } from '../../../../helpers'
import { Dictionary, KeyType } from '../../../../helpers/dictionary'
import { AlarmDefinitionService } from '../../../../services/alarm-definition'

export class AlarmDefinitionGroupRowEditController
    implements ng.IComponentController
    , IAlarmDefinitionGroupRowEdit {
    // #region Consts

    public get SMS_TRANSMISSION_TYPE(): Enums.TransmissionType { return Enums.TransmissionType.Text }
    public get EMAIL_TRANSMISSION_TYPE(): Enums.TransmissionType { return Enums.TransmissionType.EMail }

    // #endregion

    // #region Members

    public isUpdating: boolean
    public options: IAlarmDefinitionGroupRowEditOptions
    public transmissionTypeCheckBoxes: Dictionary<ITransmissionTypeCheckBox[]>

    private isGroupEditEnabled: boolean

    // #endregion

    // #region Ctor

    public constructor(
        private alarmDefinitionService: AlarmDefinitionService,
        private userSessionService: UserSessionService
    ) {
        this.isGroupEditEnabled = this.userSessionService.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_EDIT)
    }

    // #endregion

    // #region ng.IComponentController

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {

            this.initialize()
        }
    }

    // #endregion

    // #region Setup

    private initialize(): void {
        this.transmissionTypeCheckBoxes = new Dictionary<ITransmissionTypeCheckBox[]>(null, KeyType.Number)

        this.repaint()

        this.onInitialized()
    }

    private setOptions(): void {

    }

    // #endregion

    // #region Flags

    public isDeleted(): boolean {
        return this.options.alarmDef != null
            && this.options.alarmDef.opState == Enums.OpChangeState.Delete
    }

    // #endregion

    // #region Lifecycle

    private beginUpdate(): void {
        this.isUpdating = true
    }

    private endUpdate(): void {
        this.isUpdating = false
    }

    // #endregion

    // #region Events

    private onInitialized(): void {
        if (this.options != null && this.options.onInitialized != null) {
            this.options.onInitialized(this)
        }
    }

    private onAlarmDefUpdated(): void {
        if (this.options != null && this.options.onAlarmDefUpdated != null) {
            this.options.onAlarmDefUpdated(this)
        }
    }

    // #endregion

    // #region IAlarmDefinitionGroupRowEditController

    public repaint(): void {
        this.beginUpdate()

        this.ensureAlamrDefGroups()

        if (this.transmissionTypeCheckBoxes == null) {
            this.transmissionTypeCheckBoxes = new Dictionary<ITransmissionTypeCheckBox[]>(null, KeyType.Number)
        }

        this.transmissionTypeCheckBoxes
            .forEach((key: Enums.TransmissionType, arr: ITransmissionTypeCheckBox[]) => {
                arr.forEach((cbx: ITransmissionTypeCheckBox) => {
                    if (cbx != null && cbx.checkBox != null) {
                        cbx.checkBox.option(null)
                        cbx.checkBox = null
                        cbx.options = null
                    }
                })
            })

        this.transmissionTypeCheckBoxes = new Dictionary<ITransmissionTypeCheckBox[]>(null, KeyType.Number)

        const supportedTransmissionTypes: Enums.TransmissionType[] = this.alarmDefinitionService.getSupportedTransmissionTypes()

        this.options.alarmGroups.forEach((alarmGroup: OpAlarmGroup) => {
            const currentAlarmDefGroups: OpAlarmDefGroup[] = this.options.alarmDef.currentAlarmDefGroups.filter((currentAlarmDefGroup: OpAlarmDefGroup) => {
                return currentAlarmDefGroup.idAlarmGroup === alarmGroup.idAlarmGroup
            })

            supportedTransmissionTypes.forEach((transmissionType: Enums.TransmissionType) => {
                if (this.transmissionTypeCheckBoxes.tryGetValue(transmissionType) == null
                ) {
                    this.transmissionTypeCheckBoxes.addOrUpdate(transmissionType, [])
                }

                const group: OpAlarmDefGroup = _.find(currentAlarmDefGroups, (group: OpAlarmDefGroup) => group.idTransmissionType === transmissionType)

                const checkbox: ITransmissionTypeCheckBox = {
                    group: alarmGroup,
                    transmissionType: transmissionType,
                    checkBox: null,
                    options: {
                        value: group != null && group.opState !== Enums.OpChangeState.Delete,
                        disabled: !this.isGroupEditEnabled,
                        onValueChanged: (args: any) => {
                            this.transmissionTypeCheckBoxValueChanged({
                                args: args,
                                alarmGroup: alarmGroup,
                                transmissionType: transmissionType
                            })
                        },
                        onInitialized: (args: any) => {
                            const opts: ITransmissionTypeCheckBox = _.find(
                                this.transmissionTypeCheckBoxes.tryGetValue(transmissionType),
                                (cbx: ITransmissionTypeCheckBox) => cbx.group.idAlarmGroup == alarmGroup.idAlarmGroup
                            )
                            opts.checkBox = args.component
                        }
                    }
                }

                this.transmissionTypeCheckBoxes.tryGetValue(transmissionType).push(checkbox)
            })
        })

        this.endUpdate()
    }

    // #endregion

    // #region AlarmDefinitionGroupRowEditController

    private addAlarmGroup(groupToAdd: OpAlarmGroup, transmissionType: Enums.TransmissionType): void {
        this.beginUpdate()

        this.ensureAlamrDefGroups()

        if (this.options.alarmDef.currentAlarmDefGroups == null) {
            this.options.alarmDef.currentAlarmDefGroups = []
        }

        const currentAlarmDefGroup: OpAlarmDefGroup = this.findAlarmDefGroup(groupToAdd, transmissionType)

        if (currentAlarmDefGroup == null) {
            const newAlarmDefGroup: OpAlarmDefGroup = new OpAlarmDefGroup(null)
            newAlarmDefGroup.idAlarmGroup = groupToAdd.idAlarmGroup
            newAlarmDefGroup.idAlarmDef = this.options.alarmDef.idAlarmDef
            newAlarmDefGroup.idTransmissionType = transmissionType

            newAlarmDefGroup.alarmGroup = groupToAdd.clone(true, true, true, true)

            newAlarmDefGroup.opState = Enums.OpChangeState.New

            this.options.alarmDef.currentAlarmDefGroups.push(newAlarmDefGroup)

        } else if (currentAlarmDefGroup.opState === Enums.OpChangeState.Delete) {
            currentAlarmDefGroup.opState = Enums.OpChangeState.Modified
        }

        this.endUpdate()
    }

    private deleteAlarmDefGroup(groupToDelete: OpAlarmGroup, transmissionType: Enums.TransmissionType): void {
        this.beginUpdate()

        this.ensureAlamrDefGroups()

        const currentAlarmDefGroup: OpAlarmDefGroup = this.findAlarmDefGroup(groupToDelete, transmissionType)

        if (currentAlarmDefGroup != null) {
            if (currentAlarmDefGroup.opState === Enums.OpChangeState.New) {
                this.options.alarmDef.currentAlarmDefGroups.splice(this.options.alarmDef.currentAlarmDefGroups.indexOf(currentAlarmDefGroup), 1)
            } else {
                currentAlarmDefGroup.opState = Enums.OpChangeState.Delete
            }
        }

        this.endUpdate()
    }

    private ensureAlamrDefGroups(): void {
        if (this.options.alarmGroups == null) {
            this.options.alarmGroups = []
        }
    }

    private findAlarmDefGroup(groupToFind: OpAlarmGroup, transmissionType: Enums.TransmissionType): OpAlarmDefGroup {
        return _.find(this.options.alarmDef.currentAlarmDefGroups, (group: OpAlarmDefGroup) => {
            return group.idAlarmGroup === groupToFind.idAlarmGroup && group.idTransmissionType === transmissionType
        })
    }

    private transmissionTypeCheckBoxValueChanged(args: TransmissionTypeCheckBoxValueChangedArgs): void {
        if (args == null || args.args == null || args.alarmGroup == null) {
            return
        }

        if (args.args.value) {
            this.addAlarmGroup(args.alarmGroup, args.transmissionType)
        } else {
            this.deleteAlarmDefGroup(args.alarmGroup, args.transmissionType)
        }

        this.onAlarmDefUpdated()
    }

    // #endregion
}
